import i18n from "@/i18n";

const findPricingParameters = function (invoice) {
  for (let i = 0, len = invoice.items.length; i < len; i++) {
    if (invoice.items[i].meta?.pricing_current_parameters) {
      return invoice.items[i].meta.pricing_current_parameters;
    }
  }
};

const getTotalsPerType = function (invoice) {
  let totalsPerType = {};

  if (invoice) {
    for (const invoiceItem of invoice.items) {
      let key = invoiceItem.item_type;

      if (key in totalsPerType) {
        totalsPerType[key] += invoiceItem.total;
      } else {
        totalsPerType[key] = invoiceItem.total;
      }
    }
  }

  return totalsPerType;
};

const summarizeBorrowerInvoice = function (
  borrowerInvoice,
  options = {
    ownerName: null,
    communityName: null,
    groupCompensations: true,
    groupContributions: true,
    contributionsDetails: true,
  }
) {
  let invoice = {
    items: [],
  };

  if (!borrowerInvoice) {
    return invoice;
  }

  let totalsPerType = getTotalsPerType(borrowerInvoice);
  let pricingParameters = findPricingParameters(borrowerInvoice);
  let hasNonContributionItems = false;

  let contributionItems = [];
  // There should only be a single item for each of global and local contributions
  for (const item of borrowerInvoice?.items || []) {
    if (["loan.contribution", "loan.community_contribution"].includes(item.item_type)) {
      if (item.contribution_community_id) {
        let label = i18n.t("helpers.invoices.contribution.community.label");
        let subtext = null;
        if (options.contributionsDetails) {
          if (options.communityName) {
            label = item.meta?.pricing_name
              ? i18n.t("helpers.invoices.contribution.community.label_with_pricing", {
                  pricingName: item.meta.pricing_name,
                  communityName: options.communityName,
                })
              : i18n.t("helpers.invoices.contribution.community.label_with_name", {
                  communityName: options.communityName,
                });
          }
          subtext = item.meta?.pricing_description;
        }

        contributionItems.push({
          item_type: "loan.contribution",
          label,
          subtext,
          total: -item.total,
          class: "invoice-contribution",
        });
      } else {
        let label = i18n.t("helpers.invoices.contribution.network.label");
        let subtext = null;
        if (options.contributionsDetails) {
          if (item.meta?.pricing_name) {
            label = i18n.t("helpers.invoices.contribution.network.label_with_pricing", {
              pricingName: item.meta.pricing_name,
            });
          }
          subtext = item.meta?.pricing_description;
        }

        contributionItems.push({
          item_type: "loan.contribution",
          label,
          subtext,
          total: -item.total,
          class: "invoice-contribution",
        });
      }
    }
  }

  if (contributionItems.length > 0 && options.groupContributions) {
    invoice.items.push({
      label: i18n.t("helpers.invoices.contribution.group_label"),
      items: contributionItems,
    });
  } else {
    for (const contributionItem of contributionItems) {
      invoice.items.push(contributionItem);
    }
  }

  let compensationItems = ["price", "duration", "distance", "minimum"].reduce((acc, key) => {
    hasNonContributionItems = true;

    if (totalsPerType[`loan.${key}`]) {
      acc.push({
        item_type: `loan.${key}`,
        label: i18n.t(`helpers.invoices.loan.${key}`, pricingParameters),
        total: -totalsPerType[`loan.${key}`],
      });
    }

    return acc;
  }, []);

  if (totalsPerType["loan.expenses"]) {
    compensationItems.push({
      item_type: "loan.expenses",
      label: i18n.t("bill_items.fields.item_types.loan.expenses"),
      total: -(totalsPerType["loan.expenses"] ?? 0),
    });
  }

  if (compensationItems.length > 0 && options.groupCompensations) {
    let label = i18n.t("helpers.invoices.compensation.label");
    if (options.ownerName) {
      label = i18n.t("helpers.invoices.compensation.label_with_name", {
        ownerName: options.ownerName,
      });
    }
    invoice.items.push({
      label,
      items: compensationItems,
    });
  } else {
    for (const compensationItem of compensationItems) {
      invoice.items.push(compensationItem);
    }
  }

  if (totalsPerType["loan.insurance"]) {
    hasNonContributionItems = true;
    invoice.items.push({
      item_type: "loan.insurance",
      label: i18n.t("bill_items.fields.item_types.loan.insurance"),
      total: -totalsPerType["loan.insurance"],
    });
  }

  if (totalsPerType["donation.loan"]) {
    invoice.items.push({
      item_type: "donation.loan",
      label: i18n.t("bill_items.fields.item_types.donation.loan"),
      total: -totalsPerType["donation.loan"],
    });
  }

  if (hasNonContributionItems) {
    invoice.items.push({
      class: "invoice-total",
      label: i18n.t("helpers.invoices.total.label"),
      total: -borrowerInvoice.user_balance_change,
    });
  }

  return invoice;
};

const summarizeOwnerInvoice = function (ownerInvoice, borrowerName) {
  let itemsPerType = {
    "loan.price": {
      item_type: "loan.price",
      label: i18n.t("bill_items.fields.item_types.loan.price"),
      total: 0,
    },
    "loan.expenses": {
      item_type: "loan.expenses",
      label: i18n.t("bill_items.fields.item_types.loan.expenses"),
      total: 0,
    },
  };

  const totalsPerType = getTotalsPerType(ownerInvoice);

  let total = 0;
  for (const key in totalsPerType) {
    if (itemsPerType[key]) {
      itemsPerType[key].total = totalsPerType[key];
    } else {
      itemsPerType[key] = {
        item_type: key,
        label: i18n.t(`bill_items.fields.item_types.${key}`),
        total: totalsPerType[key],
      };
    }

    total += totalsPerType[key];
  }

  const items = Object.values(itemsPerType);

  items.push({
    class: "invoice-total",
    label: i18n.t("helpers.invoices.compensation.total_label", {
      borrowerName: borrowerName,
    }),
    total: total,
  });

  return {
    items,
  };
};

export { getTotalsPerType, summarizeBorrowerInvoice, summarizeOwnerInvoice };
